import { createFileRoute, useBlocker, useNavigate, useParams } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Drawer,
  FileButton,
  Group,
  Loader,
  Modal,
  NumberInput,
  PasswordInput,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowsSplit,
  IconBookmark,
  IconCheck,
  IconClock,
  IconEdit,
  IconForms,
  IconRefresh,
  IconSend,
  IconImageInPicture,
  IconMessage,
  IconPlayerPlay,
  IconPlayerStop,
  IconRobot,
  IconSparkles,
  IconTemplate,
  IconUpload,
  IconHandClick,
  IconLayoutGrid,
} from '@tabler/icons-react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  type Node,
  type Edge,
  type OnConnect,
  type OnEdgesChange,
  type OnNodesChange,
  Handle,
  Position,
} from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { CollapseRail } from '../../components/CollapseRail';
import { usePanelCollapse } from '../../components/inbox/use-panel-collapse';
import { FlowTemplatePanel } from '../../components/flow-builder/FlowTemplatePanel';
import { buildTokenOptions, TokenTextInput } from '../../components/flow-builder/TokenTextInput';
import {
  BranchPanel,
  CapturePanel,
  InteractivePanel,
  TagPanel,
} from '../../components/flow-builder/FlowNodePanels';

/**
 * Visual flow builder — port of Blazor FlowCanvas + NodePalette + properties
 * panel, all in a single React Flow surface (the .NET version split it across
 * three Razor components but in React Flow they're sibling arrangements).
 *
 * Node-type catalog mirrors `FlowNodeCatalog.DraggableTypes`:
 *   simple / media / interactive / template / ai_prompt / branch / wait /
 *   capture / tag / end (+ implicit start).
 */

const NODE_CATALOG = [
  { id: 'simple', label: 'Simple Text', icon: IconMessage, color: 'blue' },
  { id: 'media', label: 'Media', icon: IconImageInPicture, color: 'cyan' },
  { id: 'interactive', label: 'Buttons / List', icon: IconHandClick, color: 'green' },
  { id: 'template', label: 'Template', icon: IconTemplate, color: 'orange' },
  { id: 'send_form', label: 'Send Form', icon: IconForms, color: 'pink' },
  { id: 'ai_prompt', label: 'AI Prompt', icon: IconRobot, color: 'grape' },
  { id: 'branch', label: 'Branch', icon: IconArrowsSplit, color: 'indigo' },
  { id: 'wait', label: 'Wait', icon: IconClock, color: 'gray' },
  { id: 'capture', label: 'Capture Input', icon: IconEdit, color: 'teal' },
  { id: 'tag', label: 'Tag Contact', icon: IconBookmark, color: 'lime' },
  { id: 'end', label: 'End Flow', icon: IconPlayerStop, color: 'red' },
] as const;
type NodeTypeId = (typeof NODE_CATALOG)[number]['id'] | 'start';

// ── Custom Node Components ───────────────────────────────────────────────

function BaseNode({
  title,
  icon: Icon,
  color,
  selected,
  children,
  isStart,
  isEnd,
}: {
  title: string;
  icon: any;
  color: string;
  selected?: boolean;
  children?: React.ReactNode;
  isStart?: boolean;
  isEnd?: boolean;
}) {
  return (
    <Box style={{ position: 'relative' }}>
      {!isStart && (
        <Handle
          type="target"
          position={Position.Top}
          style={{ background: 'var(--mantine-color-gray-5)' }}
        />
      )}
      <Card
        withBorder
        shadow={selected ? 'md' : 'xs'}
        radius="md"
        p={0}
        style={{
          minWidth: 180,
          maxWidth: 250,
          borderColor: selected ? `var(--mantine-color-${color}-5)` : undefined,
          borderWidth: selected ? 2 : 1,
          overflow: 'visible',
        }}
      >
        <Box
          p="xs"
          style={{
            backgroundColor: `var(--mantine-color-${color}-light)`,
            borderTopLeftRadius: 'var(--mantine-radius-md)',
            borderTopRightRadius: 'var(--mantine-radius-md)',
            borderBottom: `1px solid var(--mantine-color-${color}-light-hover)`,
          }}
        >
          <Group gap="xs">
            <Icon size={14} color={`var(--mantine-color-${color}-6)`} />
            <Text
              size="xs"
              fw={700}
              c={`var(--mantine-color-${color}-light-color)`}
              style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              {title}
            </Text>
          </Group>
        </Box>
        <Box p="xs">{children}</Box>
      </Card>
      {!isEnd && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: 'var(--mantine-color-gray-5)' }}
        />
      )}
    </Box>
  );
}

function SimpleTextNode({ data, selected }: { data: any; selected?: boolean }) {
  return (
    <Box style={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: 'var(--mantine-color-gray-5)' }}
      />
      <Box
        style={{
          backgroundColor: 'white',
          border: selected
            ? '2px solid var(--mantine-color-blue-5)'
            : '1px solid var(--mantine-color-gray-3)',
          borderRadius: '12px',
          borderTopLeftRadius: '0px', // Chat bubble effect
          padding: '8px 12px',
          minWidth: 150,
          maxWidth: 220,
          boxShadow: selected ? 'var(--mantine-shadow-md)' : 'var(--mantine-shadow-xs)',
        }}
      >
        <Group justify="space-between" mb={4}>
          <Text size="10px" fw={700} c="blue.7">
            Message
          </Text>
          <IconMessage size={12} color="var(--mantine-color-blue-3)" />
        </Group>
        <Text
          size="sm"
          lineClamp={3}
          c={data.replyText ? 'black' : 'dimmed'}
          fs={!data.replyText ? 'italic' : undefined}
        >
          {data.replyText || 'No message text set...'}
        </Text>
      </Box>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: 'var(--mantine-color-gray-5)' }}
      />
    </Box>
  );
}

function MediaNode({ data, selected }: { data: any; selected?: boolean }) {
  const url: string | undefined = data.mediaUrl;
  const kind: string | undefined = data.mediaType;
  return (
    <BaseNode title="Media" icon={IconImageInPicture} color="cyan" selected={selected}>
      <Stack gap={4}>
        <Box
          style={{
            height: 80,
            backgroundColor: 'var(--mantine-color-gray-1)',
            borderRadius: 'var(--mantine-radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed var(--mantine-color-gray-3)',
            overflow: 'hidden',
          }}
        >
          {url && kind === 'image' ? (
            <img
              src={url}
              alt={data.fileName ?? 'media'}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : url && kind === 'video' ? (
            <video src={url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : url && kind === 'audio' ? (
            <Stack gap={2} align="center">
              <IconImageInPicture size={20} color="var(--mantine-color-cyan-6)" />
              <Text size="10px" c="dimmed">
                Audio
              </Text>
            </Stack>
          ) : url ? (
            <Stack gap={2} align="center">
              <IconImageInPicture size={20} color="var(--mantine-color-cyan-6)" />
              <Text size="10px" c="dimmed">
                Document
              </Text>
            </Stack>
          ) : (
            <IconImageInPicture size={24} color="var(--mantine-color-gray-4)" />
          )}
        </Box>
        <Text size="xs" fw={500} truncate>
          {data.fileName ? data.fileName : url ? String(url).split('/').pop() : 'No media selected'}
        </Text>
      </Stack>
    </BaseNode>
  );
}

function TemplateNode({ data, selected }: { data: any; selected?: boolean }) {
  return (
    <BaseNode title="Template" icon={IconTemplate} color="orange" selected={selected}>
      <Stack gap={4}>
        <Badge variant="outline" color="orange" size="xs">
          Meta Approved
        </Badge>
        <Text size="sm" fw={600} truncate>
          {data.templateName || 'Select template...'}
        </Text>
      </Stack>
    </BaseNode>
  );
}

function SendFormNode({ data, selected }: { data: any; selected?: boolean }) {
  return (
    <BaseNode title="Send Form" icon={IconForms} color="pink" selected={selected}>
      <Stack gap={4}>
        <Badge variant="outline" color="pink" size="xs">
          WhatsApp Flow
        </Badge>
        <Text size="sm" fw={600} truncate>
          {data.flowName || data.flowUid || 'Select form...'}
        </Text>
      </Stack>
    </BaseNode>
  );
}

function InteractiveNode({ data, selected }: { data: any; selected?: boolean }) {
  const isList = String(data.interactiveMode ?? 'buttons').toLowerCase() === 'list';
  const buttons = (data.buttons || []) as string[];
  const rows = ((data.rows || []) as Array<{ title?: string }>).map((r) => r?.title ?? '');
  // Ports mirror the runtime: btn_<i> for buttons, row_<i> for list rows.
  const options = isList ? rows : buttons;
  const portPrefix = isList ? 'row' : 'btn';
  return (
    <Box style={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: 'var(--mantine-color-gray-5)' }}
      />
      <Card
        withBorder
        shadow={selected ? 'md' : 'xs'}
        radius="md"
        p={0}
        style={{
          minWidth: 180,
          maxWidth: 250,
          borderColor: selected ? 'var(--mantine-color-green-5)' : undefined,
          borderWidth: selected ? 2 : 1,
        }}
      >
        <Box
          p="xs"
          style={{
            backgroundColor: 'var(--mantine-color-green-light)',
            borderBottom: '1px solid var(--mantine-color-green-light-hover)',
          }}
        >
          <Group gap="xs">
            <IconHandClick size={14} color="var(--mantine-color-green-6)" />
            <Text
              size="xs"
              fw={700}
              c="var(--mantine-color-green-light-color)"
              style={{ textTransform: 'uppercase' }}
            >
              Interactive
            </Text>
          </Group>
        </Box>
        <Box p="xs">
          <Stack gap={4}>
            <Text
              size="sm"
              lineClamp={2}
              c={data.replyText ? undefined : 'dimmed'}
              fs={!data.replyText ? 'italic' : undefined}
            >
              {data.replyText || 'No question set...'}
            </Text>
            {options.length > 0 && (
              <Stack gap={2} mt={4}>
                {options.map((opt, i) => (
                  <Box
                    key={i}
                    p={4}
                    style={{
                      backgroundColor: 'white',
                      border: '1px solid var(--mantine-color-green-2)',
                      borderRadius: '4px',
                      fontSize: '10px',
                      textAlign: 'center',
                      position: 'relative',
                    }}
                  >
                    <Group gap={4} justify="center" wrap="nowrap">
                      <Badge size="xs" variant="filled" color="green" circle>
                        {i + 1}
                      </Badge>
                      <Text size="10px" truncate>
                        {opt || (isList ? `Row ${i + 1}` : `Button ${i + 1}`)}
                      </Text>
                    </Group>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>
      </Card>
      {/* Per-button source handles distributed along the bottom edge, each
          paired with a numbered chip rendered just below the dot so the
          port→button mapping is visible on the canvas (not only in the
          node body). Edge sourceHandle ids (`btn_${i}`) are unchanged. */}
      {options.map((_, i) => {
        const leftPct = ((i + 0.5) / options.length) * 100;
        return (
          <span key={`${portPrefix}_${i}`}>
            <Handle
              type="source"
              position={Position.Bottom}
              id={`${portPrefix}_${i}`}
              style={{
                left: `${leftPct}%`,
                background: 'var(--mantine-color-green-5)',
                width: 10,
                height: 10,
              }}
            />
            <Box
              style={{
                position: 'absolute',
                bottom: -20,
                left: `${leftPct}%`,
                transform: 'translateX(-50%)',
                fontSize: 9,
                fontWeight: 700,
                color: 'white',
                background: 'var(--mantine-color-green-6)',
                padding: '1px 6px',
                borderRadius: 8,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {i + 1}
            </Box>
          </span>
        );
      })}
      {/* "Default / continue" port — wire this to advance the flow on ANY
          selection without connecting every option. The runtime uses it as
          the fallback when the tapped option has no edge of its own. */}
      {options.length > 0 && (
        <span>
          <Handle
            type="source"
            position={Position.Right}
            id="default"
            style={{ background: 'var(--mantine-color-gray-6)', width: 10, height: 10 }}
          />
          <Box
            style={{
              position: 'absolute',
              top: '50%',
              right: -46,
              transform: 'translateY(-50%)',
              fontSize: 9,
              fontWeight: 700,
              color: 'white',
              background: 'var(--mantine-color-gray-6)',
              padding: '1px 6px',
              borderRadius: 8,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            any
          </Box>
        </span>
      )}
    </Box>
  );
}

function CaptureNode({ data, selected }: { data: any; selected?: boolean }) {
  return (
    <Box style={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: 'var(--mantine-color-gray-5)' }}
      />
      <Card
        withBorder
        shadow={selected ? 'md' : 'xs'}
        radius="md"
        p={0}
        style={{
          minWidth: 180,
          maxWidth: 250,
          borderColor: selected ? 'var(--mantine-color-teal-5)' : undefined,
          borderWidth: selected ? 2 : 1,
        }}
      >
        <Box
          p="xs"
          style={{
            backgroundColor: 'var(--mantine-color-teal-light)',
            borderBottom: '1px solid var(--mantine-color-teal-light-hover)',
          }}
        >
          <Group gap="xs">
            <IconEdit size={14} color="var(--mantine-color-teal-6)" />
            <Text
              size="xs"
              fw={700}
              c="var(--mantine-color-teal-light-color)"
              style={{ textTransform: 'uppercase' }}
            >
              Capture Data
            </Text>
          </Group>
        </Box>
        <Box p="xs">
          <Stack gap={4}>
            <Text
              size="sm"
              lineClamp={2}
              c={data.replyText ? undefined : 'dimmed'}
              fs={!data.replyText ? 'italic' : undefined}
            >
              {data.replyText || 'No question set...'}
            </Text>
            <Badge variant="light" color="teal" size="xs" fullWidth>
              Save to: {data.variable || 'none'}
            </Badge>
          </Stack>
        </Box>
      </Card>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: 'var(--mantine-color-teal-5)' }}
      />
    </Box>
  );
}

function AiPromptNode({ data, selected }: { data: any; selected?: boolean }) {
  return (
    <Box style={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: 'var(--mantine-color-gray-5)' }}
      />
      <Card
        withBorder
        shadow={selected ? 'md' : 'xs'}
        radius="md"
        p={0}
        style={{
          minWidth: 180,
          maxWidth: 250,
          borderColor: selected ? 'var(--mantine-color-grape-5)' : undefined,
          borderWidth: selected ? 2 : 1,
        }}
      >
        <Box
          p="xs"
          style={{
            backgroundColor: 'var(--mantine-color-grape-light)',
            borderBottom: '1px solid var(--mantine-color-grape-light-hover)',
          }}
        >
          <Group gap="xs">
            <IconRobot size={14} color="var(--mantine-color-grape-6)" />
            <Text
              size="xs"
              fw={700}
              c="var(--mantine-color-grape-light-color)"
              style={{ textTransform: 'uppercase' }}
            >
              AI Prompt
            </Text>
          </Group>
        </Box>
        <Box p="xs">
          <Stack gap={4}>
            <Group gap={4}>
              <IconSparkles size={12} color="var(--mantine-color-grape-6)" />
              <Text size="xs" fw={600} c="var(--mantine-color-grape-light-color)">
                AI Processing
              </Text>
            </Group>
            <Text
              size="xs"
              lineClamp={2}
              c={data.prompt ? undefined : 'dimmed'}
              fs={!data.prompt ? 'italic' : undefined}
            >
              {data.prompt || 'No instructions set...'}
            </Text>
          </Stack>
        </Box>
      </Card>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: 'var(--mantine-color-grape-5)' }}
      />
    </Box>
  );
}

function StartNode({ data, selected }: { data: any; selected?: boolean }) {
  return (
    <Box style={{ position: 'relative' }}>
      <Badge
        size="xl"
        variant="filled"
        color="green"
        radius="xl"
        leftSection={<IconPlayerPlay size={16} />}
        style={{
          height: 40,
          paddingLeft: 15,
          paddingRight: 15,
          border: selected ? '2px solid var(--mantine-color-blue-5)' : 'none',
          boxShadow: 'var(--mantine-shadow-xs)',
        }}
      >
        {data.label || 'Start Flow'}
      </Badge>
      <Handle type="source" position={Position.Bottom} />
    </Box>
  );
}

function EndNode({ selected }: { selected?: boolean }) {
  return (
    <Box style={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: 'var(--mantine-color-red-5)' }}
      />
      <Badge
        size="xl"
        variant="filled"
        color="red"
        radius="xl"
        leftSection={<IconPlayerStop size={16} />}
        style={{
          height: 40,
          paddingLeft: 15,
          paddingRight: 15,
          border: selected ? '2px solid var(--mantine-color-blue-5)' : 'none',
          boxShadow: 'var(--mantine-shadow-xs)',
        }}
      >
        End Flow
      </Badge>
    </Box>
  );
}

function BranchNode({ data, selected }: { data: any; selected?: boolean }) {
  return (
    <Box style={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: 'var(--mantine-color-indigo-5)' }}
      />
      <Box
        style={{
          width: 100,
          height: 100,
          backgroundColor: 'var(--mantine-color-indigo-0)',
          border: `2px solid var(--mantine-color-indigo-${selected ? '6' : '3'})`,
          transform: 'rotate(45deg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          boxShadow: selected ? 'var(--mantine-shadow-md)' : 'var(--mantine-shadow-xs)',
        }}
      >
        <Box style={{ transform: 'rotate(-45deg)', textAlign: 'center' }}>
          <Stack gap={0} align="center">
            <IconArrowsSplit size={20} color="var(--mantine-color-indigo-6)" />
            <Text
              size="10px"
              fw={700}
              c="var(--mantine-color-indigo-light-color)"
              style={{ textTransform: 'uppercase' }}
            >
              BRANCH
            </Text>
            <Text size="9px" fw={500} truncate style={{ maxWidth: 70 }}>
              {data.label}
            </Text>
          </Stack>
        </Box>
      </Box>

      {/* Two fixed outcome ports. The runtime routes by fromPortKey
          'true' / 'false' (condition met / not met). */}
      {(
        [
          { key: 'true', label: 'Yes', leftPct: 30, color: 'teal' },
          { key: 'false', label: 'No', leftPct: 70, color: 'red' },
        ] as const
      ).map((port) => (
        <span key={port.key}>
          <Handle
            type="source"
            position={Position.Bottom}
            id={port.key}
            style={{
              left: `${port.leftPct}%`,
              background: `var(--mantine-color-${port.color}-5)`,
              width: 10,
              height: 10,
            }}
          />
          <Box
            style={{
              position: 'absolute',
              bottom: -20,
              left: `${port.leftPct}%`,
              transform: 'translateX(-50%)',
              fontSize: 9,
              fontWeight: 700,
              color: 'white',
              background: `var(--mantine-color-${port.color}-6)`,
              padding: '1px 6px',
              borderRadius: 8,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {port.label}
          </Box>
        </span>
      ))}
    </Box>
  );
}

function WaitNode({ data, selected }: { data: any; selected?: boolean }) {
  return (
    <Box style={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: 'var(--mantine-color-gray-5)' }}
      />
      <Badge
        size="xl"
        variant="filled"
        color="gray"
        radius="xl"
        leftSection={<IconClock size={16} />}
        style={{
          height: 40,
          paddingLeft: 15,
          paddingRight: 15,
          border: selected ? '2px solid var(--mantine-color-blue-5)' : 'none',
          boxShadow: 'var(--mantine-shadow-xs)',
        }}
      >
        Wait: {data.duration || '0'} {data.unit || 'm'}
      </Badge>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: 'var(--mantine-color-gray-5)' }}
      />
    </Box>
  );
}

function TagNode({ data, selected }: { data: any; selected?: boolean }) {
  return (
    <Box style={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: 'var(--mantine-color-gray-5)' }}
      />
      <Card
        withBorder
        shadow={selected ? 'md' : 'xs'}
        radius="md"
        p={0}
        style={{
          minWidth: 160,
          borderColor: selected ? 'var(--mantine-color-lime-5)' : undefined,
          borderWidth: selected ? 2 : 1,
        }}
      >
        <Box
          p="xs"
          style={{
            backgroundColor: 'var(--mantine-color-lime-light)',
            borderBottom: '1px solid var(--mantine-color-lime-light-hover)',
          }}
        >
          <Group gap="xs">
            <IconBookmark size={14} color="var(--mantine-color-lime-6)" />
            <Text
              size="xs"
              fw={700}
              c="var(--mantine-color-lime-light-color)"
              style={{ textTransform: 'uppercase' }}
            >
              Tag Contact
            </Text>
          </Group>
        </Box>
        <Box p="xs">
          <Badge variant="dot" color="lime" size="sm" fullWidth>
            {data.tag || 'Select tag...'}
          </Badge>
        </Box>
      </Card>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: 'var(--mantine-color-lime-5)' }}
      />
    </Box>
  );
}

const nodeTypes = {
  simple: SimpleTextNode,
  media: MediaNode,
  template: TemplateNode,
  send_form: SendFormNode,
  start: StartNode,
  interactive: InteractiveNode,
  ai_prompt: AiPromptNode,
  branch: BranchNode,
  wait: WaitNode,
  capture: CaptureNode,
  tag: TagNode,
  end: EndNode,
};

// ── Preview Components ──────────────────────────────────────────────────

function WhatsAppPreview({
  text,
  mediaUrl,
  mediaType,
  fileName,
  type,
  buttons = [],
  variable,
}: {
  text?: string;
  mediaUrl?: string;
  mediaType?: string;
  fileName?: string;
  type: string;
  buttons?: string[];
  variable?: string;
}) {
  return (
    <Box p="md" style={{ backgroundColor: '#e5ddd5', borderRadius: '8px', minHeight: 100 }}>
      <Stack gap="xs">
        <Box
          p="xs"
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            borderTopLeftRadius: 0,
            maxWidth: '90%',
            boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
            position: 'relative',
          }}
        >
          {type === 'media' && (
            <Box
              mb="xs"
              style={{
                aspectRatio: '16/9',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {mediaUrl && mediaType === 'image' ? (
                <img
                  src={mediaUrl}
                  alt={fileName ?? 'media'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : mediaUrl && mediaType === 'video' ? (
                <video
                  src={mediaUrl}
                  controls
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : mediaUrl && mediaType === 'audio' ? (
                <audio src={mediaUrl} controls style={{ width: '90%' }} />
              ) : mediaUrl ? (
                <Stack gap={4} align="center">
                  <IconImageInPicture size={28} color="#888" />
                  <Text size="xs" c="dimmed" ta="center" style={{ wordBreak: 'break-all' }}>
                    {fileName ?? 'Document attached'}
                  </Text>
                </Stack>
              ) : (
                <IconImageInPicture size={32} color="#ccc" />
              )}
            </Box>
          )}
          <Text size="sm" style={{ whiteSpace: 'pre-wrap', color: '#111' }}>
            {text || (type === 'template' ? 'Template content...' : 'No message text...')}
          </Text>
          {type === 'capture' && (
            <Box
              mt="xs"
              p={6}
              style={{
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                borderLeft: '3px solid #009688',
              }}
            >
              <Text size="xs" c="teal" fw={700}>
                Capturing Response
              </Text>
              <Text size="10px" c="dimmed">
                Save to: {variable || '?'}
              </Text>
            </Box>
          )}
          <Group justify="flex-end" gap={4} mt={4}>
            <Text size="10px" c="dimmed">
              10:00 AM
            </Text>
            <IconCheck size={10} color="#4fc3f7" />
          </Group>
        </Box>

        {type === 'interactive' &&
          buttons.map((btn, i) => (
            <Box
              key={i}
              p={8}
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#00a5f4',
                fontWeight: 600,
                fontSize: '13px',
                boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                cursor: 'default',
              }}
            >
              {btn || 'Button label...'}
            </Box>
          ))}
      </Stack>
    </Box>
  );
}

interface BuilderState {
  nodes: Node[];
  edges: Edge[];
}

// ── Properties Panel Component ───────────────────────────────────────────

function PropertiesPanel({
  selectedNode,
  allNodes,
  allEdges,
  updateNodeData,
  deleteNode,
}: {
  selectedNode: Node | null;
  allNodes: Node[];
  allEdges: Edge[];
  updateNodeData: (id: string, data: any) => void;
  deleteNode: (id: string) => void;
}) {
  const [draft, setDraft] = useState<any>(null);
  const [isDragging, setIsDragging] = useState(false);
  const uploadMedia = trpc.bots.flowMedia.upload.useMutation();
  const formsList = trpc.forms.list.useQuery();

  // Personalization tokens: static contact fields + a {{field.<name>}} entry
  // for each capture node that runs BEFORE this one (an answer captured
  // downstream isn't available yet, so offering it would mislead).
  const tokenOptions = useMemo(() => {
    const ancestors = selectedNode ? ancestorNodeIds(selectedNode.id, allEdges) : null;
    const captures = allNodes
      .filter((n) => (n.data as { type?: string } | undefined)?.type === 'capture')
      .filter((n) => !ancestors || ancestors.has(n.id))
      .map((n) => {
        const d = n.data as { variable?: string; label?: string };
        return { name: String(d.variable ?? '').trim(), label: d.label };
      })
      .filter((c) => c.name.length > 0);
    return buildTokenOptions(captures);
  }, [allNodes, allEdges, selectedNode]);

  // Sync draft with selected node whenever it changes.
  useEffect(() => {
    if (selectedNode) {
      setDraft({ ...selectedNode.data });
    } else {
      setDraft(null);
    }
  }, [selectedNode?.id]);

  if (!selectedNode || !draft) {
    return (
      <Stack align="center" justify="center" style={{ height: 200 }} gap="xs">
        <IconHandClick size={32} color="var(--mantine-color-gray-4)" />
        <Text c="dimmed" size="sm" ta="center">
          Select a node on the canvas to edit its properties.
        </Text>
      </Stack>
    );
  }

  const handleBlur = () => {
    updateNodeData(selectedNode.id, draft);
  };

  const updateDraft = (key: string, value: any) => {
    setDraft((prev: any) => ({ ...prev, [key]: value }));
  };

  const inferMediaType = (mime: string): 'image' | 'video' | 'document' | 'audio' => {
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const handleMediaFile = async (file: File | null) => {
    if (!file || !selectedNode) return;
    if (file.size > 100 * 1024 * 1024) {
      notifications.show({ message: 'File exceeds 100 MB limit', color: 'red' });
      return;
    }
    const buf = await file.arrayBuffer();
    const base64 = bufferToBase64(buf);
    uploadMedia.mutate(
      { base64, mimeType: file.type, filename: file.name },
      {
        onSuccess: (r) => {
          const next = {
            ...draft,
            mediaAssetId: r.assetId,
            mediaUrl: URL.createObjectURL(file),
            mediaType: inferMediaType(file.type),
            fileName: r.fileName,
            fileSize: r.fileSize,
            mimeType: r.mimeType,
          };
          setDraft(next);
          updateNodeData(selectedNode.id, next);
          notifications.show({ message: 'Media uploaded', color: 'green' });
        },
        onError: (err) => notifications.show({ message: err.message, color: 'red' }),
      },
    );
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={5} style={{ textTransform: 'capitalize' }}>
          {draft.type} Node
        </Title>
        <Tooltip label="Delete node">
          <ActionIcon color="red" variant="light" onClick={() => deleteNode(selectedNode.id)}>
            <IconPlayerStop size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Divider />

      <TextInput
        label="Node Label"
        description="Internal name for this step"
        value={draft.label ?? ''}
        onChange={(e) => updateDraft('label', e.currentTarget.value)}
        onBlur={handleBlur}
      />

      {draft.type === 'start' && (
        <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="xs">
            The trigger for this flow (e.g., keyword) is managed in the <b>Flow Settings</b>. This
            node marks where the automation logic begins.
          </Text>
        </Alert>
      )}

      {draft.type === 'simple' && (
        <Stack gap="md">
          <TokenTextInput
            label="Message Content"
            description="Static text or personalization variables (insert with the { } button)"
            placeholder="Type your message here..."
            multiline
            value={draft.replyText ?? ''}
            onChange={(v) => updateDraft('replyText', v)}
            onBlur={handleBlur}
            options={tokenOptions}
          />

          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Live Preview
            </Text>
            <WhatsAppPreview type="simple" text={draft.replyText} />
          </Stack>
        </Stack>
      )}

      {draft.type === 'media' && (
        <Stack gap="md">
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Media File
            </Text>
            <Text size="xs" c="dimmed">
              Meta supports one media file per message (Image, Video, Document, or Audio).
            </Text>
            <FileButton onChange={handleMediaFile} accept="image/*,video/*,audio/*,application/pdf">
              {(props) => (
                <Box
                  {...props}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) void handleMediaFile(file);
                  }}
                  style={{
                    position: 'relative',
                    border: isDragging
                      ? '2px solid var(--mantine-color-blue-5)'
                      : '2px dashed var(--mantine-color-gray-4)',
                    borderRadius: 'var(--mantine-radius-md)',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: isDragging
                      ? 'var(--mantine-color-blue-0)'
                      : 'var(--mantine-color-gray-0)',
                    cursor: uploadMedia.isPending ? 'progress' : 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <Group justify="center" gap="xs" style={{ pointerEvents: 'none' }}>
                    {uploadMedia.isPending ? (
                      <>
                        <Loader size="xs" />
                        <Text size="sm" fw={500} c="dimmed">
                          Uploading…
                        </Text>
                      </>
                    ) : draft.fileName ? (
                      <>
                        <IconImageInPicture size={24} color="var(--mantine-color-blue-6)" />
                        <Text size="sm" fw={500}>
                          {draft.fileName}
                        </Text>
                      </>
                    ) : (
                      <>
                        <IconUpload size={24} color="var(--mantine-color-gray-5)" />
                        <Text size="sm" fw={500} c="dimmed">
                          {isDragging ? 'Drop file to upload' : 'Click to browse or drop file here'}
                        </Text>
                      </>
                    )}
                  </Group>
                </Box>
              )}
            </FileButton>
            {(draft.fileName || draft.mediaUrl) && !uploadMedia.isPending && (
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  {draft.mimeType ?? ''}
                  {draft.fileSize ? ` • ${(draft.fileSize / 1024).toFixed(1)} KB` : ''}
                </Text>
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={() => {
                    const next = {
                      ...draft,
                      mediaAssetId: '',
                      mediaUrl: '',
                      mediaType: '',
                      fileName: '',
                      fileSize: 0,
                      mimeType: '',
                    };
                    setDraft(next);
                    updateNodeData(selectedNode.id, next);
                  }}
                >
                  Remove Media
                </Button>
              </Group>
            )}
          </Stack>

          <Textarea
            label="Caption (Optional)"
            description="Text sent along with the media file"
            minRows={2}
            autosize
            placeholder="Add a caption..."
            value={draft.replyText ?? ''}
            onChange={(e) => updateDraft('replyText', e.currentTarget.value)}
            onBlur={handleBlur}
          />

          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Live Preview
            </Text>
            <WhatsAppPreview
              type="media"
              mediaUrl={draft.mediaUrl}
              mediaType={draft.mediaType}
              fileName={draft.fileName}
              text={draft.replyText}
            />
          </Stack>
        </Stack>
      )}

      {draft.type === 'template' && (
        <FlowTemplatePanel
          data={draft as Record<string, unknown>}
          tokenOptions={tokenOptions}
          onChange={(patch) => {
            const next = { ...draft, ...patch };
            setDraft(next);
            updateNodeData(selectedNode.id, next);
          }}
        />
      )}

      {draft.type === 'send_form' && (
        <Stack gap="md">
          <Select
            label="Form to send"
            description="Sends a WhatsApp Flow (form). Published forms reach anyone; drafts deliver only to test numbers."
            placeholder="Choose a form..."
            searchable
            data={(formsList.data ?? []).map((f) => ({
              value: f.uid,
              label: f.status === 'PUBLISHED' ? f.name : `${f.name} (${f.status})`,
            }))}
            value={draft.flowUid ?? null}
            onChange={(v) => {
              const picked = (formsList.data ?? []).find((f) => f.uid === v);
              const newData = { ...draft, flowUid: v ?? '', flowName: picked?.name ?? '' };
              setDraft(newData);
              updateNodeData(selectedNode.id, newData);
            }}
          />
          {!draft.flowUid ? (
            <Alert color="gray" variant="light">
              Pick a form to send. Create and publish forms under <b>Forms</b>.
            </Alert>
          ) : (
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Live Preview
              </Text>
              <WhatsAppPreview type="template" text={`📋 ${draft.flowName || draft.flowUid}`} />
            </Stack>
          )}
        </Stack>
      )}

      {draft.type === 'interactive' && (
        <InteractivePanel
          data={draft as Record<string, unknown>}
          tokenOptions={tokenOptions}
          onChange={(patch) => {
            const next = { ...draft, ...patch };
            setDraft(next);
            updateNodeData(selectedNode.id, next);
          }}
        />
      )}

      {draft.type === 'capture' && (
        <CapturePanel
          data={draft as Record<string, unknown>}
          tokenOptions={tokenOptions}
          onChange={(patch) => {
            const next = { ...draft, ...patch };
            setDraft(next);
            updateNodeData(selectedNode.id, next);
          }}
        />
      )}

      {draft.type === 'ai_prompt' && (
        <Stack gap="md">
          <Textarea
            label="AI Prompt Context"
            description="Specific instructions for the AI model at this step"
            minRows={6}
            autosize
            placeholder="e.g. Help the user choose a plan based on their budget..."
            value={draft.prompt ?? ''}
            onChange={(e) => updateDraft('prompt', e.currentTarget.value)}
            onBlur={handleBlur}
          />
          <Alert color="grape" variant="light">
            The AI model will use this context along with the vendor's main system prompt.
          </Alert>

          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Live Preview
            </Text>
            <WhatsAppPreview
              type="simple"
              text="[AI will generate a dynamic response based on instructions...]"
            />
          </Stack>
        </Stack>
      )}

      {draft.type === 'branch' && (
        <BranchPanel
          data={draft as Record<string, unknown>}
          onChange={(patch) => {
            const next = { ...draft, ...patch };
            setDraft(next);
            updateNodeData(selectedNode.id, next);
          }}
        />
      )}

      {draft.type === 'wait' && (
        <Stack gap="md">
          <Group grow>
            <NumberInput
              label="Duration"
              min={1}
              value={draft.duration ?? 1}
              onChange={(v) => {
                updateDraft('duration', v);
                updateNodeData(selectedNode.id, { ...draft, duration: v });
              }}
            />
            <Select
              label="Unit"
              data={[
                { value: 'm', label: 'Minutes' },
                { value: 'h', label: 'Hours' },
                { value: 'd', label: 'Days' },
              ]}
              value={draft.unit ?? 'm'}
              onChange={(v) => {
                updateDraft('unit', v);
                updateNodeData(selectedNode.id, { ...draft, unit: v });
              }}
            />
          </Group>
        </Stack>
      )}

      {draft.type === 'tag' && (
        <TagPanel
          data={draft as Record<string, unknown>}
          onChange={(patch) => {
            const next = { ...draft, ...patch };
            setDraft(next);
            updateNodeData(selectedNode.id, next);
          }}
        />
      )}

      {/* Fallback for nodes that don't need configuration */}
      {draft.type === 'end' && (
        <Alert color="red" variant="light">
          <Text size="xs">This node terminates the flow. No further steps will execute.</Text>
        </Alert>
      )}
    </Stack>
  );
}

/** All node ids that can reach `targetId` following edges backward — i.e.
 *  the nodes that run before it. Used to scope {{field.*}} token options to
 *  upstream capture nodes. Cycle-safe via the visited set. */
function ancestorNodeIds(targetId: string, edges: Edge[]): Set<string> {
  const incoming = new Map<string, string[]>();
  for (const e of edges) {
    const arr = incoming.get(e.target) ?? [];
    arr.push(e.source);
    incoming.set(e.target, arr);
  }
  const seen = new Set<string>();
  const queue = [targetId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const src of incoming.get(id) ?? []) {
      if (!seen.has(src)) {
        seen.add(src);
        queue.push(src);
      }
    }
  }
  return seen;
}

// Dagre layered layout — see plan immutable-sauteeing-pearl.md.
// We re-create the graph from scratch on every call (cheap; <100 nodes
// expected) so stale ranks from a previous arrangement don't influence the
// next one — keeps the operation deterministic.
const DEFAULT_NODE_W = 220;
const DEFAULT_NODE_H = 90;

function layoutNodes(nodes: Node[], edges: Edge[], direction: 'TB' | 'LR' = 'TB'): Node[] {
  if (nodes.length === 0) return nodes;
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: direction, nodesep: 60, ranksep: 90 });
  for (const n of nodes) {
    g.setNode(n.id, {
      width: n.measured?.width ?? DEFAULT_NODE_W,
      height: n.measured?.height ?? DEFAULT_NODE_H,
    });
  }
  // Add edges in source-handle order so dagre's barycenter sweep tends to
  // place port-1 child to the left of port-2's, port-3's, etc.
  const sortedEdges = [...edges].sort((a, b) => {
    if (a.source !== b.source) return 0;
    return String(a.sourceHandle ?? '').localeCompare(String(b.sourceHandle ?? ''));
  });
  for (const e of sortedEdges) g.setEdge(e.source, e.target);
  dagre.layout(g);

  // Pull positions into a map so the post-process can mutate them.
  const positions = new Map<string, { x: number; y: number }>();
  for (const n of nodes) {
    const pos = g.node(n.id);
    if (pos) positions.set(n.id, { x: pos.x, y: pos.y });
  }

  // Post-process: for any node with multiple `sourceHandle`-tagged outgoing
  // edges, ensure its immediate same-rank children are sorted left-to-right
  // by source-handle index. dagre's crossing-minimisation can flip them.
  // We only swap x-coords within a rank — sub-trees do not move, which is
  // imperfect but acceptable for typical bot-flow shapes.
  const outBySource = new Map<string, Edge[]>();
  for (const e of edges) {
    if (!e.sourceHandle) continue;
    const arr = outBySource.get(e.source) ?? [];
    arr.push(e);
    outBySource.set(e.source, arr);
  }
  for (const [, srcEdges] of outBySource) {
    if (srcEdges.length < 2) continue;
    const ordered = [...srcEdges]
      .sort((a, b) => String(a.sourceHandle).localeCompare(String(b.sourceHandle)))
      .map((e) => e.target)
      .filter((t) => positions.has(t));
    if (ordered.length < 2) continue;
    // Group by rank (rounded y) so we only swap siblings, not cousins.
    const byRank = new Map<number, string[]>();
    for (const t of ordered) {
      const y = Math.round(positions.get(t)!.y);
      const arr = byRank.get(y) ?? [];
      arr.push(t);
      byRank.set(y, arr);
    }
    for (const [, group] of byRank) {
      if (group.length < 2) continue;
      const xs = group.map((id) => positions.get(id)!.x).sort((a, b) => a - b);
      group.forEach((id, idx) => {
        positions.set(id, { x: xs[idx]!, y: positions.get(id)!.y });
      });
    }
  }

  return nodes.map((n) => {
    const pos = positions.get(n.id);
    if (!pos) return n;
    const w = n.measured?.width ?? DEFAULT_NODE_W;
    const h = n.measured?.height ?? DEFAULT_NODE_H;
    // dagre returns the node centre; React Flow wants top-left.
    return { ...n, position: { x: pos.x - w / 2, y: pos.y - h / 2 } };
  });
}

// ── Flow test simulator (client-side) ───────────────────────────────────
// Walks the CURRENT canvas (unsaved edits included) like a WhatsApp chat so
// the builder can preview how a flow behaves before publishing. AI-prompt,
// branch conditions and capture validators only run server-side in
// production, so those are approximated here and clearly labelled.

type SimAwait =
  | { type: 'interactive'; nodeId: string }
  | { type: 'branch'; nodeId: string; options: { label: string; port: string }[] }
  | { type: 'capture'; nodeId: string; variable?: string }
  | null;

interface SimMessage {
  id: number;
  role: 'bot' | 'user' | 'system';
  kind: 'text' | 'media' | 'template' | 'flow' | 'interactive' | 'note';
  text?: string;
  nodeId?: string;
  buttons?: string[];
  header?: string;
  footer?: string;
  cta?: string;
  fileName?: string;
  mediaType?: string;
}

function SimBubble({
  m,
  awaiting,
  onButton,
}: {
  m: SimMessage;
  awaiting: SimAwait;
  onButton: (nodeId: string, label: string, idx: number) => void;
}) {
  if (m.role === 'system') {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          style={{
            background: 'rgba(0,0,0,0.06)',
            color: '#444',
            fontSize: 11,
            padding: '3px 10px',
            borderRadius: 8,
            maxWidth: '85%',
            textAlign: 'center',
          }}
        >
          {m.text}
        </Box>
      </Box>
    );
  }
  const isUser = m.role === 'user';
  const interactiveActive =
    m.kind === 'interactive' && awaiting?.type === 'interactive' && awaiting.nodeId === m.nodeId;
  return (
    <Box style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <Stack gap={4} style={{ maxWidth: '85%' }}>
        <Box
          style={{
            background: isUser ? '#dcf8c6' : 'white',
            borderRadius: 8,
            padding: '6px 9px',
            boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
            color: '#111',
          }}
        >
          {m.kind === 'media' && (
            <Group gap={6} mb={m.text ? 4 : 0}>
              <IconImageInPicture size={16} color="#888" />
              <Text size="xs" c="dimmed">
                {m.fileName || m.mediaType || 'Media'}
              </Text>
            </Group>
          )}
          {m.kind === 'template' && (
            <Badge
              size="xs"
              variant="light"
              color="orange"
              mb={4}
              leftSection={<IconTemplate size={10} />}
            >
              Template
            </Badge>
          )}
          {m.kind === 'flow' && m.header && (
            <Text size="sm" fw={700}>
              {m.header}
            </Text>
          )}
          <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {m.kind === 'template'
              ? m.text || 'Template message'
              : m.text || (m.kind === 'flow' ? 'Form' : '')}
          </Text>
          {m.kind === 'flow' && m.footer && (
            <Text size="xs" c="dimmed">
              {m.footer}
            </Text>
          )}
        </Box>

        {m.kind === 'interactive' &&
          (m.buttons ?? []).map((b, i) => (
            <Box
              key={i}
              onClick={() => interactiveActive && onButton(m.nodeId!, b, i)}
              style={{
                background: 'white',
                borderRadius: 8,
                padding: 8,
                textAlign: 'center',
                color: '#00a5f4',
                fontWeight: 600,
                fontSize: 13,
                boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                cursor: interactiveActive ? 'pointer' : 'default',
                opacity: interactiveActive ? 1 : 0.55,
              }}
            >
              {b}
            </Box>
          ))}

        {m.kind === 'flow' && (
          <Box
            style={{
              background: 'white',
              borderRadius: 8,
              padding: 8,
              color: '#00a5f4',
              fontWeight: 600,
              fontSize: 13,
              boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <IconForms size={15} /> {m.cta || 'Open form'}
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function FlowTestChat({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) {
  const [messages, setMessages] = useState<SimMessage[]>([]);
  const [awaiting, setAwaiting] = useState<SimAwait>(null);
  const [ended, setEnded] = useState(false);
  const [input, setInput] = useState('');
  const idRef = useRef(0);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const nextId = () => (idRef.current += 1);

  const outgoing = useCallback(
    (nodeId: string, port?: string): string | null => {
      const cands = edges.filter((e) => e.source === nodeId);
      if (port !== undefined) {
        return cands.find((e) => (e.sourceHandle ?? '') === port)?.target ?? null;
      }
      return (cands.find((e) => !e.sourceHandle) ?? cands[0])?.target ?? null;
    },
    [edges],
  );

  const run = useCallback(
    (startId: string | null) => {
      const out: SimMessage[] = [];
      let nextAwait: SimAwait = null;
      let didEnd = false;
      let cursor = startId;
      let guard = 0;
      while (cursor && guard++ < 200) {
        const node = nodeById.get(cursor);
        if (!node) {
          out.push({
            id: nextId(),
            role: 'system',
            kind: 'note',
            text: '⚠ Dead end — nothing connected here. Flow stops.',
          });
          didEnd = true;
          break;
        }
        const d = (node.data ?? {}) as Record<string, any>;
        const type = String(d.type ?? node.type ?? 'simple');
        if (type === 'start') {
          cursor = outgoing(node.id);
          continue;
        }
        if (type === 'simple' || type === 'ai_prompt') {
          out.push({
            id: nextId(),
            role: 'bot',
            kind: 'text',
            text:
              type === 'ai_prompt'
                ? '🤖 AI-generated reply (simulated — not run in test)'
                : d.replyText || '(empty message)',
          });
          cursor = outgoing(node.id);
          continue;
        }
        if (type === 'media') {
          out.push({
            id: nextId(),
            role: 'bot',
            kind: 'media',
            mediaType: d.mediaType,
            fileName: d.fileName,
            text: d.replyText,
          });
          cursor = outgoing(node.id);
          continue;
        }
        if (type === 'template') {
          out.push({ id: nextId(), role: 'bot', kind: 'template', text: d.templateName });
          cursor = outgoing(node.id);
          continue;
        }
        if (type === 'flow' || type === 'send_form') {
          out.push({
            id: nextId(),
            role: 'bot',
            kind: 'flow',
            text: d.flowName || d.replyText,
            cta: d.flowCta || 'Open form',
          });
          cursor = outgoing(node.id);
          continue;
        }
        if (type === 'tag') {
          out.push({
            id: nextId(),
            role: 'system',
            kind: 'note',
            text: `🏷 Tagged contact: ${d.tag || '(no tag set)'}`,
          });
          cursor = outgoing(node.id);
          continue;
        }
        if (type === 'wait') {
          out.push({
            id: nextId(),
            role: 'system',
            kind: 'note',
            text: `⏳ Waits ${d.duration ?? 0} ${d.unit || 'min'} — skipped in test`,
          });
          cursor = outgoing(node.id);
          continue;
        }
        if (type === 'interactive') {
          const buttons = ((d.buttons ?? []) as string[]).map((b, i) => b || `Button ${i + 1}`);
          out.push({
            id: nextId(),
            role: 'bot',
            kind: 'interactive',
            text: d.replyText || '(no prompt)',
            buttons,
            nodeId: node.id,
          });
          nextAwait = { type: 'interactive', nodeId: node.id };
          break;
        }
        if (type === 'capture') {
          out.push({ id: nextId(), role: 'bot', kind: 'text', text: d.replyText || '(no prompt)' });
          nextAwait = { type: 'capture', nodeId: node.id, variable: d.variable };
          break;
        }
        if (type === 'branch') {
          const branches = (d.branches ?? []) as any[];
          const options =
            branches.length > 0
              ? branches.map((b, i) => ({
                  label: String(b?.label || b?.value || `Path ${i + 1}`),
                  port: `branch_${i}`,
                }))
              : edges
                  .filter((e) => e.source === node.id)
                  .map((e, i) => ({ label: `Path ${i + 1}`, port: e.sourceHandle ?? '' }));
          out.push({
            id: nextId(),
            role: 'system',
            kind: 'note',
            text: '🔀 Branch (simulated) — pick a path below',
          });
          nextAwait = { type: 'branch', nodeId: node.id, options };
          break;
        }
        if (type === 'end') {
          out.push({ id: nextId(), role: 'system', kind: 'note', text: '✅ Flow ended' });
          didEnd = true;
          break;
        }
        out.push({
          id: nextId(),
          role: 'bot',
          kind: 'text',
          text: d.replyText || `(${type} node)`,
        });
        cursor = outgoing(node.id);
      }
      if (guard >= 200) {
        out.push({
          id: nextId(),
          role: 'system',
          kind: 'note',
          text: '⚠ Stopped — the flow looped 200+ times.',
        });
      }
      setMessages((prev) => [...prev, ...out]);
      setAwaiting(nextAwait);
      if (didEnd) setEnded(true);
    },
    [nodeById, outgoing, edges],
  );

  const restart = useCallback(() => {
    idRef.current = 0;
    setMessages([]);
    setAwaiting(null);
    setEnded(false);
    setInput('');
    const start =
      nodes.find((n) => String((n.data as any)?.type ?? n.type) === 'start') ?? nodes[0];
    if (!start) {
      setMessages([
        { id: nextId(), role: 'system', kind: 'note', text: 'No nodes on the canvas to test.' },
      ]);
      setEnded(true);
      return;
    }
    run(start.id);
  }, [nodes, run]);

  // Run once on mount — the Drawer remounts this component on each open, so it
  // always captures the latest (unsaved) canvas snapshot.
  useEffect(() => {
    restart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const v = viewportRef.current;
    if (v) v.scrollTo({ top: v.scrollHeight, behavior: 'smooth' });
  }, [messages, awaiting]);

  const answerInteractive = (nodeId: string, label: string, idx: number) => {
    setMessages((prev) => [...prev, { id: nextId(), role: 'user', kind: 'text', text: label }]);
    setAwaiting(null);
    run(outgoing(nodeId, `btn_${idx}`));
  };
  const answerBranch = (nodeId: string, opt: { label: string; port: string }) => {
    setMessages((prev) => [
      ...prev,
      { id: nextId(), role: 'system', kind: 'note', text: `→ ${opt.label}` },
    ]);
    setAwaiting(null);
    run(outgoing(nodeId, opt.port));
  };
  const submitCapture = () => {
    if (awaiting?.type !== 'capture' || !input.trim()) return;
    const val = input.trim();
    const nodeId = awaiting.nodeId;
    setMessages((prev) => [...prev, { id: nextId(), role: 'user', kind: 'text', text: val }]);
    setInput('');
    setAwaiting(null);
    run(outgoing(nodeId));
  };

  return (
    <Stack gap="sm" style={{ height: '100%' }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Text size="xs" c="dimmed">
          Simulates the current canvas (including unsaved edits). AI, branch conditions and
          validators are approximated.
        </Text>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={restart}>
          Restart
        </Button>
      </Group>

      <ScrollArea
        viewportRef={viewportRef}
        style={{ flex: 1, background: '#e5ddd5', borderRadius: 8 }}
        p="sm"
      >
        <Stack gap={8}>
          {messages.map((m) => (
            <SimBubble key={m.id} m={m} awaiting={awaiting} onButton={answerInteractive} />
          ))}
        </Stack>
      </ScrollArea>

      <Box>
        {ended ? (
          <Button
            fullWidth
            variant="light"
            leftSection={<IconRefresh size={14} />}
            onClick={restart}
          >
            Run again
          </Button>
        ) : awaiting?.type === 'capture' ? (
          <Group gap="xs" wrap="nowrap">
            <TextInput
              style={{ flex: 1 }}
              placeholder={
                awaiting.variable
                  ? `Type a reply (saved to ${awaiting.variable})…`
                  : 'Type a reply…'
              }
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitCapture();
              }}
            />
            <ActionIcon size="lg" onClick={submitCapture} aria-label="Send reply">
              <IconSend size={16} />
            </ActionIcon>
          </Group>
        ) : awaiting?.type === 'branch' ? (
          <Group gap="xs">
            {awaiting.options.map((o) => (
              <Button
                key={o.port}
                size="xs"
                variant="light"
                color="indigo"
                onClick={() => answerBranch(awaiting.nodeId, o)}
              >
                {o.label}
              </Button>
            ))}
          </Group>
        ) : awaiting?.type === 'interactive' ? (
          <Text size="xs" c="dimmed" ta="center">
            Tap a button above to continue ↑
          </Text>
        ) : null}
      </Box>
    </Stack>
  );
}

function BuilderInner({ flowId }: { flowId: number }) {
  const flow = trpc.bots.flows.getById.useQuery({ id: flowId });
  const schemaQ = trpc.bots.flows.getSchema.useQuery({ id: flowId });
  const utils = trpc.useUtils();
  const save = trpc.bots.flows.saveSchema.useMutation({
    onSuccess: (r) => {
      void utils.bots.flows.list.invalidate();
      notifications.show({
        color: 'green',
        message: `Saved (${r.derived} derived, ${r.orphaned} orphaned)`,
      });
    },
    onError: (err) =>
      notifications.show({
        color: err.data?.code === 'CONFLICT' ? 'orange' : 'red',
        message: err.message,
      }),
  });

  const [state, setState] = useState<BuilderState>({ nodes: [], edges: [] });
  const [selected, setSelected] = useState<Node | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const { leftCollapsed, rightCollapsed, toggleLeft, toggleRight } = usePanelCollapse('botbuilder');
  const { fitView } = useReactFlow();
  const navigate = useNavigate();

  // Serialize the canvas exactly as persist() saves it, so unsaved edits can be
  // detected by comparing the current canvas against the last-saved baseline.
  const buildSchema = useCallback(
    (s: BuilderState) => ({
      version: 1 as const,
      nodes: s.nodes.map((n) => ({
        id: n.id,
        type: n.type ?? 'simple',
        x: n.position.x,
        y: n.position.y,
        properties: n.data,
      })),
      edges: s.edges.map((e) => ({
        id: e.id,
        fromNodeId: e.source,
        toNodeId: e.target,
        ...(e.sourceHandle ? { fromPortKey: e.sourceHandle } : {}),
      })),
    }),
    [],
  );
  const serializeSchema = useCallback(
    (s: BuilderState) => JSON.stringify(buildSchema(s)),
    [buildSchema],
  );

  // Unsaved-changes guard. `baseline` is the last-saved snapshot; `dirty` drives
  // both the confirm modal and one blocker that covers the Back button, sidebar
  // links, and browser refresh/close.
  const [baseline, setBaseline] = useState<string | null>(null);
  const currentSerialized = useMemo(() => serializeSchema(state), [serializeSchema, state]);
  const dirty = baseline !== null && currentSerialized !== baseline;
  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });
  const handleBack = () => navigate({ to: '/vendor/bots/flows' });

  const autoArrange = useCallback(() => {
    setState((s) => {
      if (s.nodes.length === 0) return s;
      return { ...s, nodes: layoutNodes(s.nodes, s.edges, 'TB') };
    });
    // Wait one paint so React Flow re-measures the moved nodes, then fit.
    setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 50);
  }, [fitView]);

  // Block Chrome's default "open the dropped file" behaviour anywhere on the
  // builder route — the Media node drop zone calls preventDefault for itself,
  // but if the user misses the zone the browser would otherwise navigate away.
  useEffect(() => {
    const block = (e: DragEvent) => {
      // Files only; ignore internal React Flow drags.
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', block);
    window.addEventListener('drop', block);
    return () => {
      window.removeEventListener('dragover', block);
      window.removeEventListener('drop', block);
    };
  }, []);

  const updateNodeData = useCallback((id: string, data: any) => {
    setState((s) => ({
      ...s,
      nodes: s.nodes.map((n) => (n.id === id ? { ...n, data } : n)),
    }));
  }, []);

  const deleteNode = useCallback((id: string) => {
    setState((s) => ({
      nodes: s.nodes.filter((n) => n.id !== id),
      edges: s.edges.filter((e) => e.source !== id && e.target !== id),
    }));
    setSelected(null);
  }, []);

  // Hydrate from server schema on first load.
  useEffect(() => {
    if (schemaQ.data) {
      const nodes: Node[] = schemaQ.data.nodes.map((n) => {
        // Spread all saved properties back wholesale. The old cherry-pick
        // dropped keys it didn't enumerate (mediaAssetId, mediaType, fileName,
        // languageCode, bodyVariables, headerMedia, condition, …) so opening
        // and re-saving a flow silently stripped media/template bindings.
        const props = (n.properties ?? {}) as Record<string, unknown>;
        const data: Record<string, unknown> = {
          ...props,
          type: n.type as NodeTypeId,
          label: props.label ?? n.type,
        };
        // Migrate the legacy template-variable property name so the new
        // panel picks up flows authored before real parameter support.
        if (data.bodyVariables === undefined && Array.isArray(props.templateVariables)) {
          data.bodyVariables = props.templateVariables;
        }
        return {
          id: n.id,
          type: n.type, // Use specific type from DB
          position: { x: n.x, y: n.y },
          data,
        };
      });
      const edges: Edge[] = schemaQ.data.edges.map((e) => ({
        id: e.id,
        source: e.fromNodeId,
        target: e.toNodeId,
        ...(e.fromPortKey ? { sourceHandle: e.fromPortKey } : {}),
      }));
      // Ensure a `start` node exists.
      if (!nodes.find((n) => n.id === 'start')) {
        nodes.unshift({
          id: 'start',
          type: 'start',
          position: { x: 50, y: 50 },
          data: { type: 'start', label: 'Start' },
        });
      }
      setState({ nodes, edges });
      setBaseline(serializeSchema({ nodes, edges }));
    } else if (schemaQ.isFetched && !schemaQ.data) {
      const initial: BuilderState = {
        nodes: [
          {
            id: 'start',
            type: 'start',
            position: { x: 50, y: 50 },
            data: { type: 'start', label: 'Start' },
          },
        ],
        edges: [],
      };
      setState(initial);
      setBaseline(serializeSchema(initial));
    }
  }, [schemaQ.data, schemaQ.isFetched, serializeSchema]);

  const onNodesChange: OnNodesChange = useCallback((changes) => {
    setState((s) => ({ ...s, nodes: applyNodeChanges(changes, s.nodes) }));
  }, []);
  const onEdgesChange: OnEdgesChange = useCallback((changes) => {
    setState((s) => ({ ...s, edges: applyEdgeChanges(changes, s.edges) }));
  }, []);
  const onConnect: OnConnect = useCallback((conn) => {
    setState((s) => ({ ...s, edges: addEdge(conn, s.edges) }));
  }, []);

  function addNodeOfType(type: (typeof NODE_CATALOG)[number]['id']) {
    const id = `n_${Math.random().toString(36).slice(2, 10)}`;
    const meta = NODE_CATALOG.find((n) => n.id === type)!;
    const x = 250 + Math.random() * 200;
    const y = 200 + Math.random() * 200;
    setState((s) => ({
      ...s,
      nodes: [
        ...s.nodes,
        {
          id,
          type, // Use the actual type ID for React Flow component selection
          position: { x, y },
          data: {
            type,
            label: meta.label,
            replyText: '',
            mediaUrl: '',
            mediaAssetId: '',
            mediaType: '',
            fileName: '',
            fileSize: 0,
            mimeType: '',
            templateName: '',
            flowUid: '',
            flowName: '',
          },
        },
      ],
    }));
  }

  async function persist(): Promise<boolean> {
    try {
      await save.mutateAsync({ id: flowId, schema: buildSchema(state) });
      setBaseline(serializeSchema(state));
      return true;
    } catch {
      return false; // save.onError already surfaced the message
    }
  }

  if (flow.isPending || schemaQ.isPending) return <Skeleton height={500} />;
  if (flow.isError || !flow.data) return <Text c="red">{flow.error?.message ?? 'Not found'}</Text>;

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: `${leftCollapsed ? '0px' : '220px'} minmax(0, 1fr) ${
          rightCollapsed ? '0px' : '320px'
        }`,
        gridTemplateRows: 'minmax(0, 1fr)',
        gap: 8,
        height: 'calc(100vh - 100px)',
        overflow: 'hidden',
        transition: 'grid-template-columns 220ms ease',
      }}
    >
      {/* Palette */}
      <Card withBorder style={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
        <Stack gap="xs">
          <Title order={5}>Node Palette</Title>
          <Text size="xs" c="dimmed">
            Drag or click to add components to your flow.
          </Text>
          <Text size="xs" c="dimmed">
            Buttons, media and text run on all channels. List, Template and Send-Form are
            WhatsApp-only; on Messenger/Instagram they degrade to text.
          </Text>
          <Divider mb="xs" />
          <ScrollArea style={{ height: 'calc(100% - 60px)' }}>
            <Stack gap="xs">
              {NODE_CATALOG.map((c) => {
                const Icon = c.icon;
                return (
                  <Button
                    key={c.id}
                    variant="light"
                    color={c.color}
                    justify="flex-start"
                    fullWidth
                    leftSection={<Icon size={16} />}
                    onClick={() => addNodeOfType(c.id)}
                    styles={{ inner: { justifyContent: 'flex-start' } }}
                  >
                    {c.label}
                  </Button>
                );
              })}
            </Stack>
          </ScrollArea>
        </Stack>
      </Card>

      {/* Canvas */}
      <Card
        withBorder
        className="collapse-pane"
        style={{ position: 'relative', minWidth: 0, minHeight: 0, padding: 0 }}
      >
        <Group justify="space-between" p="sm">
          <Group gap="sm" wrap="nowrap">
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
              Back to list
            </Button>
            <Stack gap={2}>
              <Title order={4}>{flow.data.title}</Title>
              <Text c="dimmed" size="sm">
                {flow.data.startTrigger ? `Start trigger: ${flow.data.startTrigger}` : 'No trigger'}
                {flow.data.status === 1 ? (
                  <Badge ml="sm" color="green" variant="light">
                    active
                  </Badge>
                ) : (
                  <Badge ml="sm" color="gray" variant="light">
                    draft
                  </Badge>
                )}
              </Text>
            </Stack>
          </Group>
          <Group gap="xs">
            <Tooltip label="Auto-arrange all nodes into a clean top-down diagram">
              <Button
                variant="light"
                leftSection={<IconLayoutGrid size={14} />}
                onClick={autoArrange}
                disabled={state.nodes.length === 0}
              >
                Auto Arrange
              </Button>
            </Tooltip>
            <Tooltip label="Preview the flow as a WhatsApp chat (uses the current canvas, unsaved edits included)">
              <Button
                variant="light"
                color="teal"
                leftSection={<IconPlayerPlay size={14} />}
                onClick={() => setTestOpen(true)}
                disabled={state.nodes.length <= 1}
              >
                Test
              </Button>
            </Tooltip>
            <Button
              leftSection={<IconCheck size={14} />}
              loading={save.isPending}
              onClick={() => void persist()}
            >
              Save flow
            </Button>
          </Group>
        </Group>
        <Box style={{ height: 'calc(100% - 56px)' }}>
          <ReactFlow
            nodes={state.nodes}
            edges={state.edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={{ type: 'smoothstep' }}
            fitView
            onNodeClick={(_, node) => setSelected(node)}
            onPaneClick={() => setSelected(null)}
          >
            <Background />
            <Controls />
            <MiniMap pannable zoomable />
          </ReactFlow>
        </Box>
        <CollapseRail
          side="left"
          collapsed={leftCollapsed}
          onToggle={toggleLeft}
          label="node palette"
        />
        <CollapseRail
          side="right"
          collapsed={rightCollapsed}
          onToggle={toggleRight}
          label="properties"
        />
      </Card>

      {/* Properties panel */}
      <Card withBorder style={{ minWidth: 0, minHeight: 0, height: '100%', overflow: 'hidden' }}>
        <ScrollArea style={{ height: '100%' }}>
          <PropertiesPanel
            selectedNode={selected}
            allNodes={state.nodes}
            allEdges={state.edges}
            updateNodeData={updateNodeData}
            deleteNode={deleteNode}
          />
        </ScrollArea>
      </Card>

      {/* Test simulator — client-side WhatsApp-style preview of the flow. */}
      <Drawer
        opened={testOpen}
        onClose={() => setTestOpen(false)}
        position="right"
        size={440}
        title={<Text fw={600}>Test flow</Text>}
        overlayProps={{ backgroundOpacity: 0.15 }}
      >
        <Box style={{ height: 'calc(100vh - 110px)' }}>
          <FlowTestChat nodes={state.nodes} edges={state.edges} />
        </Box>
      </Drawer>

      <Modal
        opened={blocker.status === 'blocked'}
        onClose={() => blocker.reset?.()}
        title="Unsaved changes"
        centered
      >
        <Stack>
          <Text size="sm">You have unsaved changes to this flow. Save them before leaving?</Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => blocker.reset?.()}>
              Cancel
            </Button>
            <Button color="red" variant="light" onClick={() => blocker.proceed?.()}>
              Discard & leave
            </Button>
            <Button
              loading={save.isPending}
              onClick={async () => {
                if (await persist()) blocker.proceed?.();
              }}
            >
              Save & leave
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

const Builder = () => {
  const params = useParams({ from: '/vendor/bots/flows/$flowId/builder' });
  const id = useMemo(() => Number(params.flowId), [params.flowId]);
  return (
    <ReactFlowProvider>
      <BuilderInner flowId={id} />
    </ReactFlowProvider>
  );
};

export const Route = createFileRoute('/vendor/bots/flows/$flowId/builder')({
  component: Builder,
});
